import { mount } from './index.js'

// ---------------------------------------------------------------- placement
// A team's *placement* is the starting seat it occupies in the bracket -- the
// "W1:top" style address of one side of one first-round matchup. Seeding a
// team gives it the seat the structure suggests for that seed; placing it is
// overriding that, putting the team in a different seat than its seed asks
// for. Tournament#placements is the server side of the same idea.
//
// Manager "Teams" page: a row per site team with a seed input, and the bracket
// in editor mode where entry seats are drop targets. Seeds stay with the team;
// dragging only changes which starting seat the team occupies.
export function mountPlacementEditor(root) {
  const widget = mount(root.querySelector('[data-js-bracket]'))
  const rows = [...root.querySelectorAll('[data-js-placement-row]')]
  const entrySeats = JSON.parse(root.dataset.jsEntrySeats) // [{address, seed}]
  const suggested = Object.fromEntries(entrySeats.map(s => [s.seed, s.address]))
  const status = root.querySelector('[data-js-placement-status]')

  const rowTeam = row => JSON.parse(row.dataset.jsTeam)
  const seedOf = row => parseInt(row.querySelector('[data-js-seed]').value, 10) || null
  const seatOf = row => row.querySelector('[data-js-seat]').value || ''
  // The seat is only honored by the server while it still belongs to the
  // seed it was chosen for, so record that seed alongside it.
  const setSeat = (row, addr) => {
    row.querySelector('[data-js-seat]').value = addr || ''
    row.querySelector('[data-js-seat-seed]').value = addr ? (seedOf(row) || '') : ''
  }
  const rowAt = addr => rows.find(r => seatOf(r) === addr)

  function syncPayload() {
    const payload = widget.payload
    const byAddr = {}
    for (const row of rows) {
      const seed = seedOf(row), seat = seatOf(row)
      if (seed && seat) byAddr[seat] = { ...rowTeam(row), seed }
    }
    let placed = 0
    for (const b of payload.brackets) for (const r of b.rounds) for (const s of r.matchups) for (const side of ['top', 'bottom']) {
      const seat = s[side]
      if (seat.seed == null) continue
      seat.team = byAddr[seat.address] || null
      seat.label = seat.team ? seat.team.name : `Seed ${seat.seed}`
      if (seat.team) placed++
    }
    widget.redraw()
    if (status) status.textContent = `${placed} of ${entrySeats.length} starting seats filled`
  }

  // Seed typed: the team moves to the seat that suggests that seed, swapping
  // with whoever is there.
  function onSeedChange(row) {
    const seed = seedOf(row)
    if (!seed) { setSeat(row, ''); return syncPayload() }
    for (const other of rows) {
      if (other !== row && seedOf(other) === seed) {
        other.querySelector('[data-js-seed]').value = ''
        setSeat(other, '')
      }
    }
    const target = suggested[seed]
    if (!target) { setSeat(row, ''); return syncPayload() }
    const occupant = rowAt(target)
    const displaced = occupant && occupant !== row ? occupant : null
    const vacated = seatOf(row)
    if (displaced) setSeat(displaced, vacated)
    setSeat(row, target)
    // Nothing to swap into when the row had no seat of its own, so the
    // displaced team falls back to the seat its own seed suggests, the way the
    // server seats it on save. Skipping it when that seat is taken keeps two
    // teams from sharing one.
    if (displaced && !vacated) {
      const fallback = suggested[seedOf(displaced)]
      setSeat(displaced, fallback && !rowAt(fallback) ? fallback : '')
    }
    syncPayload()
  }
  rows.forEach(row => row.querySelector('[data-js-seed]').addEventListener('change', () => onSeedChange(row)))

  root.querySelector('[data-js-reset-seats]')?.addEventListener('click', () => {
    rows.forEach(row => setSeat(row, seedOf(row) ? suggested[seedOf(row)] || '' : ''))
    syncPayload()
  })
  root.querySelector('[data-js-clear-seeds]')?.addEventListener('click', () => {
    rows.forEach(row => { row.querySelector('[data-js-seed]').value = ''; setSeat(row, '') })
    syncPayload()
  })

  // Drag a placed team onto another starting seat to swap.
  let dragging = null
  const canvas = widget.canvas
  canvas.addEventListener('dragstart', e => {
    const el = e.target.closest('[data-js-seat][draggable="true"]')
    if (!el) return
    dragging = el.dataset.jsSeat
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', dragging)
    el.classList.add('is-dragging')
  })
  canvas.addEventListener('dragover', e => {
    const el = e.target.closest('.is-droppable')
    if (!el || !dragging) return
    e.preventDefault()
    el.classList.add('is-drop-target')
  })
  canvas.addEventListener('dragleave', e => { e.target.closest('.is-droppable')?.classList.remove('is-drop-target') })
  canvas.addEventListener('drop', e => {
    const el = e.target.closest('.is-droppable')
    if (!el || !dragging) return
    e.preventDefault()
    const from = dragging, to = el.dataset.jsSeat
    dragging = null
    if (from === to) return syncPayload()
    const a = rowAt(from), b = rowAt(to)
    if (a) setSeat(a, to)
    if (b) setSeat(b, from)
    syncPayload()
  })
  canvas.addEventListener('dragend', () => { dragging = null; syncPayload() })

  syncPayload()
}
